import { useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import Skeleton from "react-loading-skeleton";

import { Stack } from "capitalrx-components-2.0/lib/layout";
import { useAlert, Link } from "capitalrx-components-2.0/lib/general";
import { queryString, isValidLatitude, isValidLongitude, formatZip } from "capitalrx-utilities";

import { DEFAULT_MAP_COORDINATES } from "@/common/constants/mapConstants";
import { useGetPharmacyMutation } from "@/api/pharmacy";
import { PharmacyJourneyLayout } from "@/basic/layouts/PharmacyJourneyLayout";
import { PharmacyDetailsContainer, MapCoordinates, PharmacyDetailsType, Heading, Body, getAppleMapsURL } from "@/basic/components";

import { FormularyParams } from "@/basic/components/FormularyButtonContainer";

import styles from "./styles.module.scss";

export const PharmacyJourneyDetails = () => {
  const { t: translate } = useTranslation();
  const getCurrentQueryStrings = queryString.convertQueryStringToJson(location.search);

  const { formulary }: FormularyParams = useParams();
  const pharmacyInfo: Array<PharmacyDetailsType> = [];

  const [coordinates, setCoordinates] = useState<MapCoordinates>({ latitude: null, longitude: null });
  const [pharmacyData, setPharmacyData] = useState<Array<PharmacyDetailsType>>();

  const [getPharmacy, { data, isLoading, isError }] = useGetPharmacyMutation();

  const showAlert = useAlert();

  useEffect(() => {
    async function fetchData() {
      const pharmacy = await getPharmacy({
        provider_id: getCurrentQueryStrings?.provider_id?.toString(),
      });

      pharmacyInfo.push({
        // @ts-ignore
        pharmacy: pharmacy.data,
      });

      setPharmacyData(pharmacyInfo);

      setCoordinates({
        latitude: Number(getCurrentQueryStrings.lat),
        longitude: Number(getCurrentQueryStrings.lng),
      });
    }

    fetchData();


  }, [location]);

  const failureHandler = useCallback(() => {
    showAlert({ type: "error", style: "toast", message: translate("PHARMACY_JOURNEY_DETAILS.anErrorOcurred") });
  }, [showAlert]);

  if (isError) {
    failureHandler();
  }

  const center = {
    lat: DEFAULT_MAP_COORDINATES.lat,
    lng: DEFAULT_MAP_COORDINATES.lng,
  };

  if (isValidLatitude(coordinates.latitude) && isValidLongitude(coordinates.longitude)) {
    center.lat = coordinates.latitude;
    center.lng = coordinates.longitude;
  }

  const backButtonParams = queryString.convertJsonToQueryString({
    zip_code: getCurrentQueryStrings?.zip_code?.toString(),
    lat: getCurrentQueryStrings?.lat?.toString(),
    lng: getCurrentQueryStrings?.lng?.toString(),
  });

  const address = `${data?.address_line_1}, ${data?.address_line_2 && `${data?.address_line_2} , `}${data?.city},
  ${data?.state}, ${formatZip(data?.zip)}`;
  const url = getAppleMapsURL({ data: { address } });

  const topSectionContent = (
    <Stack spacing="mini" className={styles["topSectionContent"]}>
      <Heading>{isLoading ? <Skeleton /> : data?.name}</Heading>
      <Body>
        {isLoading ? (
          <Skeleton />
        ) : (
          <Link to={url}>
            {address}
          </Link>
        )}
      </Body>
    </Stack>
  );

  return (
    <PharmacyJourneyLayout
      backButtonParams={backButtonParams}
      backButtonUrl={`/${formulary}/pharmacy/list`}
      topSectionContent={topSectionContent}
      detailsPage
    >
      <PharmacyDetailsContainer pharmacyData={pharmacyData} data={data} isLoading={isLoading} center={center} />
    </PharmacyJourneyLayout>
  );
};
